import type { GridRect, ReportComponentConfig } from './types';

/**
 * What one edit did to a serialized layout, for an edit history: which
 * component it added, removed, moved, resized or edited — or `layout` when it
 * changed more than one (a generated layout). Compared by component id, so it
 * reads layouts whose ids were kept between edits.
 */
export type LayoutChange =
  | {
      kind: 'add' | 'remove' | 'move' | 'resize' | 'edit';
      component: ReportComponentConfig;
    }
  | { kind: 'layout' };

type Serialized = {
  items: readonly (GridRect & { component: ReportComponentConfig })[];
};
type Item = Serialized['items'][number];

function byId(layout: Serialized): Map<string, Item> {
  return new Map(layout.items.map((item) => [item.component.id, item]));
}

/**
 * What a move or resize did, when nothing was added, removed or edited: a
 * change of size is a resize of that component; else a move — of `actedOn`
 * when the host says which one it moved (a move pushes others aside too),
 * else of the one that moved farthest.
 */
function placeChange(
  prev: Map<string, Item>,
  next: Map<string, Item>,
  actedOn: string | undefined,
): LayoutChange {
  const pairs = [...next.values()].flatMap((item) => {
    const was = prev.get(item.component.id);
    return was ? [{ item, was }] : [];
  });
  const resized = pairs.filter(
    ({ item, was }) => item.w !== was.w || item.h !== was.h,
  );
  const [resizedOne] = resized;
  if (resized.length > 1) return { kind: 'layout' };
  if (resizedOne)
    return { kind: 'resize', component: resizedOne.item.component };
  const moved = pairs
    .map(({ item, was }) => ({
      item,
      distance: Math.abs(item.x - was.x) + Math.abs(item.y - was.y),
    }))
    .filter(({ distance }) => distance > 0);
  const chosen =
    moved.find(({ item }) => item.component.id === actedOn) ??
    moved.reduce<(typeof moved)[number] | undefined>(
      (farthest, entry) =>
        !farthest || entry.distance > farthest.distance ? entry : farthest,
      undefined,
    );
  return chosen
    ? { kind: 'move', component: chosen.item.component }
    : { kind: 'layout' };
}

export function describeLayoutChange(
  before: Serialized,
  after: Serialized,
  actedOn?: string,
): LayoutChange {
  const prev = byId(before);
  const next = byId(after);
  const added = [...next.values()].filter((i) => !prev.has(i.component.id));
  const removed = [...prev.values()].filter((i) => !next.has(i.component.id));
  if (added.length + removed.length > 1) return { kind: 'layout' };
  const [addedOne] = added;
  if (addedOne) return { kind: 'add', component: addedOne.component };
  const [removedOne] = removed;
  if (removedOne) return { kind: 'remove', component: removedOne.component };

  const edited = [...next.values()].filter(
    (item) =>
      JSON.stringify(item.component) !==
      JSON.stringify(prev.get(item.component.id)?.component),
  );
  const [editedOne] = edited;
  if (edited.length > 1) return { kind: 'layout' };
  if (editedOne) return { kind: 'edit', component: editedOne.component };

  return placeChange(prev, next, actedOn);
}
